// @flow

import React from "react";
import { Field, useField } from "react-final-form";
import type { FieldRenderProps } from "react-final-form";
import { Segment } from "semantic-ui-react";
import { changeServiceFormInteraction } from "Commons/Utilities/GoogleTagManager";
import { read } from "modules/ChangeServiceForm/misc/utilities";
import { RadioBasic, RadioGroupBasic } from "shared/formElements/Radio/RadioBasic";
import { isIban } from "Commons/Utilities/validators/isIban";
import { validateAccountBankNumber, validateAccountNumber } from "Commons/Utilities/validators/changeService";
import { UtmParams } from "Commons/Utilities/Tracking";
import { loadCustomTrackingData } from "Commons/Utilities/Tracking/TrackingHandler";
import { specialPartnersUtmSource } from "modules/ChangeService/misc/Constants";
import { normaliseIban, formatIban } from "modules/ChangeService/ChangeServices/submodules/Old_Edit/misc/helpers";
import { Row, Error, Input, Label, Space, WhenFieldChanges } from "../../shared/CommonComponents";
import Bonus from "../../shared/Bonus/Bonus";
import { composeValidators, required } from "../../../misc/validations";

type Props = {
  inheritedStyles: Object,
  fieldLabels: Object,
  stepName: string,
};

const BankDetails = ({ inheritedStyles, fieldLabels, stepName }: Props) => {
  const utmSource = loadCustomTrackingData(UtmParams.UTM_SOURCE_CC);
  const { formRow, formRowPaired, formLabelLight, errorMessage } = inheritedStyles;
  const desiredPaymentMethodField = useField("desiredPaymentMethod");

  return (
    <>
      <Space rem={0.5} />
      <Row className={formRowPaired}>
        <Field name="desiredPaymentMethod" validate={required} type="radio">
          {(props: FieldRenderProps) => {
            return (
              <RadioGroupBasic centered direction="basicColumn" inheritedStyles={inheritedStyles}>
                <RadioBasic
                  key={"csf_bank_1"}
                  label={"IBAN"}
                  name="desiredPaymentMethod"
                  value={"DIRECT_DEBIT_IBAN"}
                  onChange={(value) => {
                    changeServiceFormInteraction(stepName, "IBAN");
                    return props.input.onChange(value);
                  }}
                  selected={desiredPaymentMethodField.input.value === "DIRECT_DEBIT_IBAN"}
                  inheritedStyles={inheritedStyles}
                />
                <RadioBasic
                  key={"csf_bank_2"}
                  label={"BLZ/Kontonummer"}
                  name="desiredPaymentMethod"
                  value={"DIRECT_DEBIT_BLZ_ACCOUNT_NUMBER"}
                  onChange={(value) => {
                    changeServiceFormInteraction(stepName, "BLZ");
                    return props.input.onChange(value);
                  }}
                  selected={desiredPaymentMethodField.input.value === "DIRECT_DEBIT_BLZ_ACCOUNT_NUMBER"}
                  inheritedStyles={inheritedStyles}
                />
              </RadioGroupBasic>
            );
          }}
        </Field>
        <Error name="desiredPaymentMethod" className={errorMessage} />
      </Row>
      {desiredPaymentMethodField.input.value === "DIRECT_DEBIT_IBAN" && (
        <Row className={formRow}>
          <Label className={formLabelLight}>{read(fieldLabels.bankDetails.iban)}</Label>
          <Field
            name={"accountIban"}
            parse={normaliseIban}
            format={formatIban}
            validate={composeValidators(required, isIban)}
          >
            {(props: FieldRenderProps) => {
              return (
                <Input
                  fullWidth
                  placeholder="IBAN"
                  errorMessage={(props.meta.modified || props.meta.touched) && props.meta.error}
                  error={(props.meta.modified || props.meta.touched) && props.meta.error}
                  {...props.input}
                />
              );
            }}
          </Field>
        </Row>
      )}
      {desiredPaymentMethodField.input.value === "DIRECT_DEBIT_BLZ_ACCOUNT_NUMBER" && (
        <>
          <Row className={formRow}>
            <Label className={formLabelLight}>{read(fieldLabels.bankDetails.blz)}</Label>
            <Field name={"accountBankNumber"} validate={composeValidators(required, validateAccountBankNumber)}>
              {(props: FieldRenderProps) => {
                return (
                  <Input
                    fullWidth
                    placeholder="BLZ"
                    errorMessage={(props.meta.modified || props.meta.touched) && props.meta.error}
                    error={(props.meta.modified || props.meta.touched) && props.meta.error}
                    {...props.input}
                  />
                );
              }}
            </Field>
          </Row>
          <Row className={formRow}>
            <Label className={formLabelLight}>{read(fieldLabels.bankDetails.counterNumber)} </Label>
            <Field name={"accountNumber"} validate={composeValidators(required, validateAccountNumber)}>
              {(props: FieldRenderProps) => {
                return (
                  <Input
                    fullWidth
                    placeholder={read(fieldLabels.bankDetails.counterNumber)}
                    errorMessage={(props.meta.modified || props.meta.touched) && props.meta.error}
                    error={(props.meta.modified || props.meta.touched) && props.meta.error}
                    {...props.input}
                  />
                );
              }}
            </Field>
          </Row>
        </>
      )}
      {specialPartnersUtmSource.includes(utmSource) && (
        <>
          <Space rem={1.5} />
          <Row className={formRow}>
            <Segment color="yellow">
              <Bonus type={utmSource} showErrorMessage />
            </Segment>
          </Row>
        </>
      )}
      <WhenFieldChanges
        field="desiredPaymentMethod"
        becomes={"DIRECT_DEBIT_BLZ_ACCOUNT_NUMBER"}
        set={"accountIban"}
        to={undefined}
      />
      <WhenFieldChanges
        field="desiredPaymentMethod"
        becomes={"DIRECT_DEBIT_IBAN"}
        set={"accountNumber"}
        to={undefined}
      />
      <WhenFieldChanges
        field="desiredPaymentMethod"
        becomes={"DIRECT_DEBIT_IBAN"}
        set={"accountBankNumber"}
        to={undefined}
      />
    </>
  );
};

export default BankDetails;
